import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line,
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, CartesianGrid,
} from "recharts";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Download, Wifi, WifiOff, RefreshCw, Radio } from "lucide-react";

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const onepagerRef = useRef(null);
  const [driftRunning, setDriftRunning] = useState(false);
  const [driftResult, setDriftResult] = useState(null);
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    api.get("/admin/cascade-alerts/schedule").then(({ data }) => setSchedule(data)).catch(() => {});
  }, []);

  const runDrift = async () => {
    setDriftRunning(true);
    try {
      const { data } = await api.post("/admin/cascade-alerts/run");
      setDriftResult(data);
    } finally { setDriftRunning(false); }
  };

  const loadDomain = () =>
    api.get("/admin/domain-check").then(({ data }) => setDomain(data)).catch(() => setDomain({ status: "error" }));

  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setData(data)).finally(() => setLoading(false));
    loadDomain();
    const iv = setInterval(loadDomain, 60_000);
    return () => clearInterval(iv);
  }, []);

  const downloadOnepager = async () => {
    if (!onepagerRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(onepagerRef.current, { scale: 2, backgroundColor: "#F4EEDC" });
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const imgW = pageW - 40;
      const imgH = (canvas.height / canvas.width) * imgW;
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 20, 20, imgW, imgH);
      pdf.save(`Value-Realisation-Index-Insights.pdf`);
    } finally { setDownloading(false); }
  };

  if (loading || !data) return <div className="tick-label py-16">Loading analytics…</div>;

  const o = data.overview;
  const bandRows = Object.entries(data.bands).sort();
  const mixRows = Object.entries(data.response_mix);
  const cascadeRows = Object.entries(data.cascade_labels);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="space-y-10"
      data-testid="admin-analytics-panel"
    >
      {/* Domain health + actions strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DomainCard domain={domain} onRefresh={loadDomain} />
        <div className="md:col-span-2 border border-oxford/15 bg-linen p-6 flex items-center justify-between">
          <div>
            <div className="tick-label-accent">Question Insights One-Pager</div>
            <div className="display-serif text-2xl mt-1">Export the diagnostic-author view.</div>
            <p className="text-charcoal text-sm mt-2 max-w-lg">Downloads a printable A4 PDF showing where value is leaking systemically — perfect seed material for the 2027 Value Realisation Index research.</p>
          </div>
          <button data-testid="analytics-download-onepager" onClick={downloadOnepager} disabled={downloading} className="btn-oxford">
            <Download className="w-3.5 h-3.5" strokeWidth={1.5}/> {downloading ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Cascade drift check */}
      <div className="border border-oxford/15 bg-linen p-6 flex items-center justify-between" data-testid="drift-panel">
        <div>
          <div className="tick-label-accent flex items-center gap-2"><Radio className="w-3.5 h-3.5" strokeWidth={1.5}/>Cascade Drift Check</div>
          <div className="display-serif text-2xl mt-1">Find directors who just crossed into Disconnected.</div>
          <p className="text-charcoal text-sm mt-2 max-w-2xl">Emails Steve a summary of every board director whose Alignment Gap has newly widened past 20 points — the earliest warning signal of value leakage. Each director is only alerted once per drift event.</p>
          {schedule?.enabled && (
            <div className="mt-3 text-xs text-charcoal" data-testid="drift-schedule">
              <span className="tick-label-accent">Auto-run</span> · {schedule.cron}
              {schedule.next_run_at && <> · next: <span className="font-mono text-oxford">{new Date(schedule.next_run_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London", timeZoneName: "short" })}</span></>}
              {schedule.recipient && <> · to <span className="font-mono text-oxford">{schedule.recipient}</span></>}
            </div>
          )}
          {driftResult && (
            <div className="mt-3 text-sm font-mono text-oxford" data-testid="drift-result">
              Checked {driftResult.checked} · Newly drifted: {driftResult.newly_drifted.length}
              {driftResult.emailed_to && <span className="text-orange"> · emailed to {driftResult.emailed_to}</span>}
            </div>
          )}
        </div>
        <button data-testid="drift-run-btn" onClick={runDrift} disabled={driftRunning} className="btn-orange whitespace-nowrap">
          {driftRunning ? "Running…" : "Run now"}
        </button>
      </div>

      {/* Overview strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-oxford/10">
        <Kpi label="Submissions" value={o.total_submissions} />
        <Kpi label="Sessions" value={o.total_sessions} />
        <Kpi label="Directors" value={o.total_members} />
        <Kpi label="Leads" value={o.leads_captured} />
        <Kpi label="Conversion" value={`${o.conversion_rate}%`} />
        <Kpi label="Avg VRI" value={o.avg_vri} accent />
        <Kpi label="Median VRI" value={o.median_vri} accent />
      </div>

      {/* Growth over time */}
      <Section title="Growth over time" eyebrow="Weekly submissions">
        <div className="border border-oxford/15 bg-linen p-6" data-testid="chart-growth">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.growth} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid stroke="#0E2148" strokeOpacity={0.08} vertical={false} />
              <XAxis dataKey="week" tick={{ fill: "#3B4A6B", fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fill: "#3B4A6B", fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: "#3B4A6B", fontSize: 11 }} domain={[0, 125]} />
              <Tooltip contentStyle={{ background: "#F4EEDC", border: "1px solid #0E2148", borderRadius: 0, fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="count" name="Submissions" stroke="#0E2148" strokeWidth={2} dot={{ r: 3, fill: "#0E2148" }} />
              <Line yAxisId="right" type="monotone" dataKey="median_vri" name="Median VRI" stroke="#E86A20" strokeWidth={2} dot={{ r: 3, fill: "#E86A20" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Question insights */}
      <Section title="Where value is leaking, systemically" eyebrow="Weakest questions across all responses">
        <div className="border border-oxford/15 bg-linen" data-testid="chart-questions">
          {data.questions.slice(0, 10).map((q, i) => {
            const pct = (q.avg / 5) * 100;
            const dimColour = dimensionColour(q.dimension);
            return (
              <div key={q.id} className="border-b border-oxford/10 last:border-b-0 p-4 flex items-start gap-4">
                <div className="tick-label w-14 font-mono">Q{q.id}</div>
                <div className="flex-1">
                  <div className="text-sm text-oxford">{q.text}</div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="tick-label" style={{ color: dimColour }}>{q.dimension}</span>
                    <span className="tick-label">weight {q.weight}</span>
                    <span className="tick-label">{q.pct_low}% scored ≤ 2</span>
                  </div>
                </div>
                <div className="w-44 text-right">
                  <div className="font-mono text-lg text-oxford">{q.avg}<span className="text-charcoal text-sm">/5</span></div>
                  <div className="mt-1 h-1 bg-parchment relative">
                    <motion.div
                      initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                      style={{ background: dimColour, height: "100%" }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Dimension medians radar + Bands */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Dimension medians" eyebrow="Across every response ever">
          <div className="border border-oxford/15 bg-linen p-6" data-testid="chart-dimensions">
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={data.dimensions.map(d => ({ dim: d.dimension, score: d.median }))} outerRadius="70%">
                <PolarGrid gridType="polygon" stroke="#0E2148" strokeOpacity={0.15} />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#0E2148", fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#6D82B4", fontSize: 10 }} axisLine={false} />
                <Radar dataKey="score" stroke="#E86A20" fill="#E86A20" fillOpacity={0.22} strokeWidth={1.8} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Band distribution" eyebrow="How the VRI is landing">
          <div className="border border-oxford/15 bg-linen p-6" data-testid="chart-bands">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={bandRows.map(([b, c]) => ({ band: b.replace("Value Realisation ", "").replace("Significant ", ""), count: c }))} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                <CartesianGrid stroke="#0E2148" strokeOpacity={0.08} vertical={false} />
                <XAxis dataKey="band" tick={{ fill: "#3B4A6B", fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fill: "#3B4A6B", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#F4EEDC", border: "1px solid #0E2148", borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="count" fill="#0E2148" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      {/* Response mix + Cascade */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Response mix" eyebrow="Who is responding">
          <div className="border border-oxford/15 bg-linen p-6" data-testid="chart-mix">
            <div className="space-y-4">
              {mixRows.map(([k, v]) => {
                const total = mixRows.reduce((a, [,x]) => a + x, 0);
                const pct = total ? (v / total) * 100 : 0;
                const labels = {
                  personal: "Personal (no board session)",
                  board_member: "Board members",
                  direct_report: "Direct reports (cascade)",
                  session_participant: "Session participants",
                };
                return (
                  <div key={k}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-oxford">{labels[k] || k}</span>
                      <span className="font-mono text-oxford">{v}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-parchment relative">
                      <motion.div
                        initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute top-0 left-0 h-full bg-orange"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        <Section title="Leadership Cascade patterns" eyebrow="Across every director">
          <div className="border border-oxford/15 bg-linen p-6" data-testid="chart-cascade">
            <div className="space-y-4">
              {cascadeRows.map(([k, v]) => {
                const total = cascadeRows.reduce((a, [,x]) => a + x, 0);
                const pct = total ? (v / total) * 100 : 0;
                const colour = k === "Aligned" ? "#E86A20" : k === "Divergent" ? "#6D82B4" : k === "Disconnected" ? "#B23A1A" : "#3B4A6B";
                return (
                  <div key={k}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-oxford">{k}</span>
                      <span className="font-mono text-oxford">{v}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-parchment relative">
                      <motion.div
                        initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute top-0 left-0 h-full"
                        style={{ background: colour }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>
      </div>

      {/* Recent activity */}
      <Section title="Recent activity" eyebrow="Latest submissions">
        <div className="border border-oxford/15 bg-linen divide-y divide-oxford/10" data-testid="recent-activity">
          {data.recent.map(r => (
            <div key={r.id} className="p-4 flex items-baseline justify-between text-sm">
              <div>
                <span className="text-oxford">{r.name}</span>
                {r.organisation && <span className="text-charcoal"> · {r.organisation}</span>}
                <div className="tick-label mt-1">
                  {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  {r.is_board_member && " · Board Member"}
                  {r.is_direct_report && " · Direct Report"}
                  {r.board_session_code && ` · ${r.board_session_code}`}
                </div>
              </div>
              <div className="font-mono text-lg text-oxford">{r.vri}</div>
            </div>
          ))}
          {data.recent.length === 0 && <div className="p-8 text-center tick-label">No submissions yet</div>}
        </div>
      </Section>

      {/* Hidden A4-styled one-pager for PDF capture */}
      <div style={{ position: "absolute", left: -99999, top: 0, width: 794, background: "#F4EEDC" }}>
        <div ref={onepagerRef} style={{ width: 794, minHeight: 1123, padding: 48, background: "#F4EEDC", color: "#0E2148", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#E86A20" }}>
            Langwood360 · A Body of Work
          </div>
          <div style={{ height: 2, width: 48, background: "#E86A20", marginTop: 18 }} />
          <div style={{ fontSize: 44, lineHeight: 1.05, marginTop: 22, letterSpacing: "-0.02em" }}>
            Where value is leaking, <em style={{ color: "#E86A20" }}>systemically</em>.
          </div>
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: "#3B4A6B", marginTop: 14, maxWidth: 560, lineHeight: 1.5 }}>
            The ten questions scoring lowest across every response ever collected by the Value Realisation Index. Sample size: {o.total_submissions} responses across {o.total_sessions} organisations. Median VRI: <span style={{ color: "#0E2148" }}>{o.median_vri}</span>.
          </div>
          <div style={{ marginTop: 36 }}>
            {data.questions.slice(0, 10).map((q, i) => (
              <div key={q.id} style={{ borderTop: i === 0 ? "1px solid rgba(14,33,72,0.15)" : "none", borderBottom: "1px solid rgba(14,33,72,0.15)", padding: "12px 0", display: "flex", alignItems: "flex-start", gap: 16 }}>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "#3B4A6B", width: 40, letterSpacing: "0.14em" }}>Q{q.id}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 14, color: "#0E2148", lineHeight: 1.4 }}>{q.text}</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: dimensionColour(q.dimension), marginTop: 4 }}>
                    {q.dimension} · weight {q.weight} · {q.pct_low}% scored ≤ 2
                  </div>
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 22, color: "#0E2148", width: 80, textAlign: "right" }}>{q.avg}<span style={{ fontSize: 12, color: "#6D82B4" }}>/5</span></div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 40, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#3B4A6B", display: "flex", justifyContent: "space-between" }}>
            <span>Langwood360 · The Value Realisation Framework</span>
            <span>langwood360.com · {new Date().toISOString().slice(0, 10)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function DomainCard({ domain, onRefresh }) {
  const status = domain?.status;
  const good = status === "healthy";
  const bad = status && status !== "healthy";
  const colour = good ? "#E86A20" : bad ? "#B23A1A" : "#6D82B4";
  const label = status === "healthy" ? "Reachable" :
    status === "dns_or_unreachable" ? "DNS not resolving" :
    status === "timeout" ? "Timeout" :
    status === "degraded" ? "Degraded" : "Checking…";
  const Icon = good ? Wifi : WifiOff;
  return (
    <div className="border border-oxford/15 bg-linen p-6" data-testid="domain-health-card">
      <div className="flex items-baseline justify-between">
        <div className="tick-label-accent">Custom Domain</div>
        <button onClick={onRefresh} className="text-charcoal hover:text-oxford" title="Refresh">
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5}/>
        </button>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <Icon className="w-6 h-6" strokeWidth={1.5} style={{ color: colour }} />
        <div className="display-serif text-2xl" style={{ color: colour }}>{label}</div>
      </div>
      <div className="mt-2 text-xs text-charcoal">
        {domain?.url || "—"}
        {domain?.http_code != null && <span> · HTTP {domain.http_code}</span>}
        {domain?.latency_ms != null && <span> · {domain.latency_ms} ms</span>}
      </div>
      {domain?.message && <div className="mt-1 text-xs text-heritage italic">{domain.message}</div>}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-linen p-6">
      <div className="tick-label">{label}</div>
      <div className={`mt-2 font-mono text-3xl ${accent ? "text-orange" : "text-oxford"}`}>{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, eyebrow, children }) {
  return (
    <div>
      <div className="tick-label-accent mb-2">{eyebrow}</div>
      <div className="display-serif text-2xl md:text-3xl mb-4">{title}</div>
      {children}
    </div>
  );
}

function dimensionColour(dim) {
  return {
    Clarity: "#0E2148",
    Alignment: "#3B4A6B",
    Execution: "#6D82B4",
    "Value Realisation": "#E86A20",
    Choice: "#B23A1A",
  }[dim] || "#0E2148";
}
